import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { StatusTag } from '../Status-Tag';
import { DetailsButton } from '../Details-Button';
import { SearchInput } from '../Search-Input';
import { useTranslation } from 'react-i18next';
import {
  Container,
  FiltersRow,
  Filters,
  FilterButton,
  SearchInputWrapper,
  Table,
  Header,
  HeaderCell,
  Row,
  Cell,
  PaginationBar,
  SizeSelector,
  PageControls,
  PageButton,
} from './styles';

const STATUS_NORMALIZE = {
  PUBLISHED: 'Published',
  AWAITING_PUBLICATION: 'Awaiting publication',
};

const normalizeStatus = (status) =>
  STATUS_NORMALIZE[status?.toUpperCase?.().replace(/ /g, '_')] ?? status;

const FILTERS = [
  { id: 'all', labelKey: 'All content' },
  { id: 'Awaiting publication', labelKey: 'Content in progress' },
  { id: 'Published', labelKey: 'Published' },
];

const TABLE_HEADER_KEYS = ['Content', 'Create date', 'Type', 'Published', ''];

const SIZE_PER_PAGE_OPTIONS = [5, 10, 15, 20, 25];
const DEFAULT_PAGE_SIZE = 10;

export const ContentList = ({ items = [] }) => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = items
    ?.filter((item) => activeFilter === 'all' || normalizeStatus(item?.status) === activeFilter)
    ?.filter((item) => item?.title?.toLowerCase()?.includes(searchTerm.toLowerCase()));

  const totalPages = Math.ceil(filteredItems.length / sizePerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * sizePerPage,
    currentPage * sizePerPage,
  );

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSizeChange = (e) => {
    setSizePerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <Container>
      <FiltersRow>
        <Filters>
          {FILTERS.map((filter) => (
            <FilterButton
              key={filter.id}
              active={activeFilter === filter.id}
              onClick={() => handleFilterChange(filter.id)}
            >
              {t(filter.labelKey)}
            </FilterButton>
          ))}
        </Filters>
        <SearchInputWrapper>
          <SearchInput value={searchTerm} onChange={handleSearchChange} />
        </SearchInputWrapper>
      </FiltersRow>

      <Table>
        <Header>
          {TABLE_HEADER_KEYS.map((key, index) => (
            <HeaderCell key={index}>{key ? t(key) : ''}</HeaderCell>
          ))}
        </Header>

        {paginatedItems.map((item) => (
          <PautaRow key={item.id} content={item} />
        ))}
      </Table>

      <PaginationBar>
        <SizeSelector>
          <select value={sizePerPage} onChange={handleSizeChange}>
            {SIZE_PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
            <option value={filteredItems.length}>{t('All')}</option>
          </select>
        </SizeSelector>

        <PageControls>
          <PageButton onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1}>
            <i className="bx bx-chevrons-left" />
          </PageButton>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </PageButton>
          ))}
          <PageButton
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <i className="bx bx-chevrons-right" />
          </PageButton>
        </PageControls>
      </PaginationBar>
    </Container>
  );
};

const GUIDELINE_TYPE_KEYS = {
  BLOG_POST: 'Blog Post',
  CATEGORY: 'Category',
  PRODUCT: 'Product',
};

const PautaRow = ({ content }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const typeKey = GUIDELINE_TYPE_KEYS[content.type];
  const typeLabel = typeKey ? t(typeKey) : content.type;

  return (
    <Row>
      <Cell>{content.title}</Cell>
      <Cell>
        {content.createdAt ? new Date(content.createdAt).toLocaleDateString('pt-BR') : ''}
      </Cell>
      <Cell>{typeLabel ?? content.type}</Cell>
      <Cell>
        <StatusTag status={content.status} />
      </Cell>
      <Cell>
        <DetailsButton
          onClick={() => navigate(`/guideline/${content.id}`)}
          isLoading={content.processing}
        />
      </Cell>
    </Row>
  );
};

ContentList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      status: PropTypes.string.isRequired,
      processing: PropTypes.bool.isRequired,
    }),
  ),
};

PautaRow.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    status: PropTypes.string.isRequired,
    processing: PropTypes.bool.isRequired,
  }).isRequired,
};
